import {
  ACQUISITION_STAGE_LABEL,
  ACTIVE_CLAIM_STATUSES,
  CLAIM_STATUS_LABEL,
  LOSS_REASON_LABEL,
  LOSS_REASONS,
  OUTREACH_CHANNEL_LABEL,
  OUTREACH_CHANNELS,
  OUTREACH_OUTCOME_LABEL,
  SALES_GOAL_LABEL,
  SALES_GOALS,
  SALES_VERTICALS,
  SUBJECT_KIND_LABEL,
  VERTICAL_LABEL,
  isInPool,
  onPlatform,
  sourceKind,
  type ClaimStatus,
  type LossReason,
  type OutreachChannel,
  type OutreachFeedbackOutcome,
  type PerformancePeriod,
  type SalesClaim,
  type SalesGoal,
  type SalesOffer,
  type SalesOutreach,
  type SalesPolicy,
  type SalesPurpose,
  type SalesRep,
  type SalesSubject,
  type SalesVertical,
  type SourceKind,
  type SubjectKind,
} from '@/client/types/sales';

export type DashScope = 'floor' | 'me';
export type DashGroupBy = 'vertical' | 'goal' | 'source' | 'kind';
export type ComparePeriod = 'off' | 'prior' | '7d' | '30d' | '90d';

export const COMPARE_PERIODS: ComparePeriod[] = ['off', 'prior', '7d', '30d', '90d'];

export const COMPARE_PERIOD_LABEL: Record<ComparePeriod, string> = {
  off: 'No compare',
  prior: 'Prior window',
  '7d': 'Previous 7 days',
  '30d': 'Previous 30 days',
  '90d': 'Previous 90 days',
};

export function compareCaption(compare: ComparePeriod): string {
  if (compare === 'off') return '';
  if (compare === 'prior') return 'vs prior window';
  if (compare === '7d') return 'vs prev 7d';
  if (compare === '90d') return 'vs prev 90d';
  return 'vs prev 30d';
}

export type KpiId =
  | 'wonGhs'
  | 'pipelineGhs'
  | 'influencedSpend'
  | 'quotaLeft'
  | 'winRate'
  | 'slaHitRate'
  | 'avgDaysToWin'
  | 'claimed'
  | 'won'
  | 'lost'
  | 'expired'
  | 'outreachSent'
  | 'replyRate'
  | 'ctr'
  | 'customers'
  | 'poolAvailable'
  | 'overdue'
  | 'offerCost'
  | 'capacity'
  | 'pipelineCoverage';

export type WidgetId =
  | 'attention'
  | 'trend'
  | 'funnel'
  | 'acquisition'
  | 'breakdown'
  | 'outreach'
  | 'loss'
  | 'desks'
  | 'agents'
  | 'aging'
  | 'offers'
  | 'activity';

export interface DashConfig {
  kpis: KpiId[];
  widgets: Record<WidgetId, boolean>;
  groupBy: DashGroupBy;
  comparePeriod: ComparePeriod;
}

export const KPI_META: { id: KpiId; label: string; kind: 'ghs' | 'pct' | 'num' | 'days' }[] = [
  { id: 'wonGhs', label: 'Won', kind: 'ghs' },
  { id: 'pipelineGhs', label: 'In play', kind: 'ghs' },
  { id: 'influencedSpend', label: 'Influenced spend', kind: 'ghs' },
  { id: 'quotaLeft', label: 'Quota left', kind: 'ghs' },
  { id: 'winRate', label: 'Win rate', kind: 'pct' },
  { id: 'slaHitRate', label: 'SLA hit', kind: 'pct' },
  { id: 'avgDaysToWin', label: 'Days to win', kind: 'days' },
  { id: 'claimed', label: 'Claimed', kind: 'num' },
  { id: 'won', label: 'Wins', kind: 'num' },
  { id: 'lost', label: 'Lost', kind: 'num' },
  { id: 'expired', label: 'Expired', kind: 'num' },
  { id: 'outreachSent', label: 'Outreach sent', kind: 'num' },
  { id: 'replyRate', label: 'Reply rate', kind: 'pct' },
  { id: 'ctr', label: 'Link CTR', kind: 'pct' },
  { id: 'customers', label: 'Customers', kind: 'num' },
  { id: 'poolAvailable', label: 'Pool available', kind: 'num' },
  { id: 'overdue', label: 'Overdue', kind: 'num' },
  { id: 'offerCost', label: 'Offer cost', kind: 'ghs' },
  { id: 'capacity', label: 'Capacity used', kind: 'pct' },
  { id: 'pipelineCoverage', label: 'Pipeline cover', kind: 'pct' },
];

export const WIDGET_META: { id: WidgetId; label: string }[] = [
  { id: 'attention', label: 'Needs a touch' },
  { id: 'trend', label: 'Won value' },
  { id: 'funnel', label: 'Claim funnel' },
  { id: 'acquisition', label: 'Acquisition' },
  { id: 'breakdown', label: 'Breakdown' },
  { id: 'outreach', label: 'Outreach' },
  { id: 'loss', label: 'Loss & feedback' },
  { id: 'desks', label: 'Desks' },
  { id: 'agents', label: 'Agents' },
  { id: 'aging', label: 'Aging columns' },
  { id: 'offers', label: 'Offers' },
  { id: 'activity', label: 'Wins & follow-ups' },
];

export const DEFAULT_DASH_CONFIG: DashConfig = {
  kpis: ['wonGhs', 'pipelineGhs', 'pipelineCoverage', 'winRate', 'slaHitRate', 'overdue'],
  widgets: {
    attention: true,
    trend: true,
    funnel: true,
    acquisition: true,
    breakdown: false,
    outreach: false,
    loss: false,
    desks: true,
    agents: true,
    aging: false,
    offers: false,
    activity: true,
  },
  groupBy: 'vertical',
  comparePeriod: 'prior',
};

const STORAGE_KEY = 'ff-admin.sales.dashboard.v3';

function isKpiId(v: unknown): v is KpiId {
  return KPI_META.some((k) => k.id === v);
}

function isComparePeriod(v: unknown): v is ComparePeriod {
  return v === 'off' || v === 'prior' || v === '7d' || v === '30d' || v === '90d';
}

export function loadDashConfig(): DashConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_DASH_CONFIG;
    const parsed = JSON.parse(raw) as Partial<DashConfig> & { compare?: boolean };
    const kpis = Array.isArray(parsed.kpis) ? parsed.kpis.filter(isKpiId).slice(0, 8) : DEFAULT_DASH_CONFIG.kpis;
    const widgets = { ...DEFAULT_DASH_CONFIG.widgets, ...(parsed.widgets ?? {}) };
    const groupBy: DashGroupBy =
      parsed.groupBy === 'goal' || parsed.groupBy === 'source' || parsed.groupBy === 'kind' ? parsed.groupBy : 'vertical';
    const comparePeriod: ComparePeriod = isComparePeriod(parsed.comparePeriod)
      ? parsed.comparePeriod
      : parsed.compare === false
        ? 'off'
        : 'prior';
    return {
      kpis: kpis.length ? kpis : DEFAULT_DASH_CONFIG.kpis,
      widgets,
      groupBy,
      comparePeriod,
    };
  } catch {
    return DEFAULT_DASH_CONFIG;
  }
}

export function saveDashConfig(next: DashConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
}

export type DashGoalFilter = SalesGoal | 'all' | 'paying';

export interface DashFilters {
  period: PerformancePeriod;
  comparePeriod: ComparePeriod;
  repId?: string;
  vertical: SalesVertical | 'all';
  goal: DashGoalFilter;
  kind: SubjectKind | 'all';
}

export interface KpiValue {
  id: KpiId;
  value: number | null;
  prev: number | null;
}

export interface NamedCount {
  id: string;
  label: string;
  n: number;
  ghs: number;
}

export type OutcomeId = 'users' | 'listers' | 'paying' | 'advertisers';

export interface OutcomeInsight {
  id: OutcomeId;
  label: string;
  hint: string;
  goal: SalesGoal | null;
  won: number;
  claimed: number;
  closed: number;
  rate: number | null;
  prevWon: number | null;
  prevRate: number | null;
  inPlay: number;
  stock: number;
  history: (number | null)[];
}

export interface OutcomeSeriesPoint {
  label: string;
  users: number | null;
  listers: number | null;
  paying: number | null;
  advertisers: number | null;
}

export interface SeriesPoint {
  label: string;
  claimed: number;
  won: number;
  outreach: number;
  wonGhs: number;
}

export interface AgentInsight {
  id: string;
  name: string;
  role: SalesRep['role'];
  title: string;
  avatarColor: string;
  quotaGhs: number;
  wonGhs: number;
  pipelineGhs: number;
  influencedSpendGhs: number;
  won: number;
  outreachSent: number;
  replyRate: number;
  slaHitRate: number;
  active: number;
  overdue: number;
  attainment: number;
}

export interface DashInsights {
  fromMs: number;
  toMs: number;
  prevFromMs: number;
  prevToMs: number;
  kpis: Record<KpiId, KpiValue>;
  funnel: { id: string; label: string; n: number; ghs: number; rate: number | null }[];
  acquisition: { id: string; label: string; n: number }[];
  series: SeriesPoint[];
  outcomeSeries: OutcomeSeriesPoint[];
  byGoal: NamedCount[];
  byVertical: NamedCount[];
  bySource: NamedCount[];
  byKind: NamedCount[];
  byChannel: { id: OutreachChannel; label: string; sent: number; replied: number; clicked: number }[];
  byOutcome: { id: OutreachFeedbackOutcome; label: string; n: number }[];
  outcomes: OutcomeInsight[];
  lossReasons: { id: LossReason; label: string; n: number }[];
  desks: NamedCount[];
  offers: { id: string; name: string; n: number; costGhs: number; wonGhs: number }[];
  agents: AgentInsight[];
  overdue: SalesClaim[];
  expiringSoon: SalesClaim[];
  idle: SalesClaim[];
  recentWins: SalesClaim[];
  dueOutreach: SalesOutreach[];
  offerCostGhs: number;
  wonGhs: number;
  quotaGhs: number;
  liveN: number;
  freezePct: number;
  liveBook: { id: ClaimStatus; label: string; n: number }[];
  remainingGhs: number;
  coverage: number;
}

const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  intelligence: 'Intelligence',
  research: 'Research',
  contributed: 'Contributed',
  platform: 'On FeelyFeely',
};

function windowStart(period: PerformancePeriod, at: number): number {
  if (period === 'all') return 0;
  if (period === '7d') return at - 7 * 86400_000;
  if (period === '90d') return at - 90 * 86400_000;
  return at - 30 * 86400_000;
}

function compareWindow(
  compare: ComparePeriod,
  currentFrom: number,
  currentTo: number,
): { from: number; to: number } | null {
  if (compare === 'off') return null;
  if (compare === 'prior') {
    if (currentFrom <= 0) return null;
    const span = Math.max(1, currentTo - currentFrom);
    return { from: currentFrom - span, to: currentFrom };
  }
  const days = compare === '7d' ? 7 : compare === '90d' ? 90 : 30;
  const end = currentFrom > 0 ? currentFrom : currentTo;
  return { from: end - days * 86400_000, to: end };
}

function inRange(iso: string, from: number, to: number): boolean {
  const t = new Date(iso).getTime();
  return t >= from && t <= to;
}

function ratio(n: number, d: number): number {
  return d > 0 ? n / d : 0;
}

function claimVertical(c: SalesClaim, subject: SalesSubject | undefined, purpose: SalesPurpose | undefined): SalesVertical {
  return purpose?.vertical ?? subject?.verticals[0] ?? 'homes';
}

function lastOutreachAt(subjectId: string, outreaches: SalesOutreach[]): number | null {
  const last = outreaches
    .filter((o) => o.subjectId === subjectId && o.status !== 'draft' && o.status !== 'blocked')
    .sort((a, b) => b.at.localeCompare(a.at))[0];
  return last ? new Date(last.at).getTime() : null;
}

function tallyGoal(
  claims: SalesClaim[],
  goal: SalesGoal,
  from: number,
  to: number,
  purposeById: (id: string) => SalesPurpose | undefined,
): { won: number; claimed: number; closed: number; rate: number | null; inPlay: number } {
  const match = (c: SalesClaim) => purposeById(c.purposeId)?.goal === goal;
  const claimed = claims.filter((c) => match(c) && inRange(c.claimedAt, from, to)).length;
  const won = claims.filter((c) => match(c) && c.status === 'won' && c.win && inRange(c.win.at, from, to)).length;
  const lost = claims.filter((c) => match(c) && c.status === 'lost' && inRange(c.claimedAt, from, to)).length;
  const closed = won + lost;
  return {
    won,
    claimed,
    closed,
    rate: closed ? won / closed : null,
    inPlay: claims.filter((c) => match(c) && ACTIVE_CLAIM_STATUSES.includes(c.status)).length,
  };
}

function isPaying(s: SalesSubject): boolean {
  return (s.platform?.lifetimePaidGhs ?? 0) > 0 || s.acquisitionStage === 'customer';
}

function uniqueIds(claims: SalesClaim[]): string[] {
  return [...new Set(claims.map((c) => c.subjectId))];
}

function tallyPaying(
  claims: SalesClaim[],
  payingNow: Set<string>,
  from: number,
  to: number,
): { won: number; closed: number; rate: number | null } {
  const closed = uniqueIds(
    claims.filter(
      (c) =>
        (c.status === 'won' && c.win && inRange(c.win.at, from, to)) || (c.status === 'lost' && inRange(c.claimedAt, from, to)),
    ),
  );
  const won = uniqueIds(
    claims.filter((c) => c.status === 'won' && c.win && inRange(c.win.at, from, to) && payingNow.has(c.subjectId)),
  );
  return { won: won.length, closed: closed.length, rate: closed.length ? won.length / closed.length : null };
}

function emptyKpis(): Record<KpiId, KpiValue> {
  const out = {} as Record<KpiId, KpiValue>;
  for (const k of KPI_META) out[k.id] = { id: k.id, value: 0, prev: null };
  return out;
}

function tallyWindow(input: {
  claims: SalesClaim[];
  subjects: SalesSubject[];
  outreaches: SalesOutreach[];
  purposeById: (id: string) => SalesPurpose | undefined;
  offerById: (id: string | undefined) => SalesOffer | undefined;
  from: number;
  to: number;
  now: number;
  policy: SalesPolicy;
  repCount: number;
  quotaGhs: number;
}): {
  map: Record<KpiId, number | null>;
  funnel: DashInsights['funnel'];
  overdueClaims: SalesClaim[];
  live: SalesClaim[];
} {
  const { claims, subjects, outreaches, purposeById, offerById, from, to, now, policy, repCount, quotaGhs } = input;
  const live = claims.filter((c) => ACTIVE_CLAIM_STATUSES.includes(c.status));
  let claimed = 0;
  let firstTouched = 0;
  let won = 0;
  let lost = 0;
  let expired = 0;
  let slaHits = 0;
  let slaDenom = 0;
  let wonGhs = 0;
  let offerCost = 0;
  let winDays = 0;
  let winN = 0;
  let pipelineGhs = 0;
  const overdueClaims: SalesClaim[] = [];

  for (const c of live) {
    pipelineGhs += purposeById(c.purposeId)?.estimatedValueGhs ?? 0;
    if (new Date(c.expiresAt).getTime() <= now) overdueClaims.push(c);
  }

  for (const c of claims) {
    const purpose = purposeById(c.purposeId);
    const value = purpose?.estimatedValueGhs ?? 0;
    const offer = offerById(c.offerId);
    if (inRange(c.claimedAt, from, to)) {
      claimed += 1;
      const first = outreaches
        .filter((o) => o.claimId === c.id && o.status !== 'draft' && o.status !== 'blocked')
        .sort((a, b) => a.at.localeCompare(b.at))[0];
      const windowEnd = new Date(c.claimedAt).getTime() + policy.firstTouchHours * 3600_000;
      if (first) {
        firstTouched += 1;
        slaDenom += 1;
        if (new Date(first.at).getTime() <= windowEnd) slaHits += 1;
      } else if (now >= windowEnd) {
        slaDenom += 1;
      }
    }
    if (c.status === 'won' && c.win && inRange(c.win.at, from, to)) {
      won += 1;
      wonGhs += value;
      if (offer) offerCost += offer.costGhs;
      const days = (new Date(c.win.at).getTime() - new Date(c.claimedAt).getTime()) / 86400_000;
      if (Number.isFinite(days)) {
        winDays += days;
        winN += 1;
      }
    }
    if (c.status === 'lost' && inRange(c.claimedAt, from, to)) lost += 1;
    if (c.status === 'expired' && inRange(c.claimedAt, from, to)) expired += 1;
  }

  const sent = outreaches.filter(
    (o) => o.direction === 'outbound' && o.status !== 'draft' && o.status !== 'blocked' && inRange(o.at, from, to),
  );
  const replied = sent.filter((o) => o.status === 'replied' || o.feedback).length;
  const clicked = sent.filter(
    (o) => o.status === 'clicked' || o.status === 'replied' || o.trackedLinks.some((l) => l.events.some((e) => e.kind === 'click')),
  ).length;

  let poolAvailable = 0;
  let customers = 0;
  let influenced = 0;
  for (const s of subjects) {
    if (s.acquisitionStage === 'research') continue;
    if (s.acquisitionStage === 'customer') customers += 1;
    influenced += s.platform?.lifetimePaidGhs ?? 0;
    const locked = live.some((c) => c.subjectId === s.id);
    if (!locked && !s.doNotContact && !(s.subjectCooldownUntil && new Date(s.subjectCooldownUntil).getTime() > now)) {
      poolAvailable += 1;
    }
  }

  const closed = won + lost;
  const remaining = Math.max(0, quotaGhs - wonGhs);
  const capDenom = policy.maxConcurrentPerRep * Math.max(1, repCount);
  const map: Record<KpiId, number | null> = {
    wonGhs,
    pipelineGhs,
    influencedSpend: influenced,
    quotaLeft: Math.max(0, quotaGhs - wonGhs),
    winRate: closed ? won / closed : 0,
    slaHitRate: slaDenom ? slaHits / slaDenom : 0,
    avgDaysToWin: winN ? winDays / winN : null,
    claimed,
    won,
    lost,
    expired,
    outreachSent: sent.length,
    replyRate: ratio(replied, sent.length),
    ctr: ratio(clicked, sent.length),
    customers,
    poolAvailable,
    overdue: overdueClaims.length,
    offerCost,
    capacity: ratio(live.length, capDenom),
    pipelineCoverage: remaining > 0 ? pipelineGhs / remaining : null,
  };

  return {
    map,
    overdueClaims,
    live,
    funnel: [
      { id: 'claimed', label: 'Claimed', n: claimed, ghs: 0, rate: null },
      { id: 'touched', label: 'Touched', n: firstTouched, ghs: 0, rate: ratio(firstTouched, claimed) },
      { id: 'won', label: 'Won', n: won, ghs: wonGhs, rate: ratio(won, claimed) },
      { id: 'lost', label: 'Lost', n: lost, ghs: 0, rate: ratio(lost, claimed) },
    ],
  };
}

export function buildDashInsights(input: {
  subjects: SalesSubject[];
  claims: SalesClaim[];
  outreaches: SalesOutreach[];
  purposes: SalesPurpose[];
  offers: SalesOffer[];
  policy: SalesPolicy;
  reps: SalesRep[];
  filters: DashFilters;
  now: number;
  purposeById: (id: string) => SalesPurpose | undefined;
  offerById: (id: string | undefined) => SalesOffer | undefined;
}): DashInsights {
  const { subjects, claims, outreaches, policy, reps, filters, now, purposeById, offerById } = input;
  const to = now;
  const from = windowStart(filters.period, now);
  const compared = compareWindow(filters.comparePeriod, from, to);
  const prevFrom = compared?.from ?? 0;
  const prevTo = compared?.to ?? 0;

  const subjectOk = (s: SalesSubject) => {
    if (filters.vertical !== 'all' && !s.verticals.includes(filters.vertical)) return false;
    if (filters.kind !== 'all' && s.kind !== filters.kind) return false;
    return true;
  };

  const claimOk = (c: SalesClaim) => {
    if (filters.repId && c.ownerId !== filters.repId) return false;
    const sub = subjects.find((s) => s.id === c.subjectId);
    const purpose = purposeById(c.purposeId);
    if (filters.goal === 'paying') {
      if (!sub || !isPaying(sub)) return false;
    } else if (filters.goal !== 'all' && purpose?.goal !== filters.goal) return false;
    if (filters.vertical !== 'all' && claimVertical(c, sub, purpose) !== filters.vertical) return false;
    if (sub && !subjectOk(sub)) return false;
    return true;
  };

  const claimOkExceptGoal = (c: SalesClaim) => {
    if (filters.repId && c.ownerId !== filters.repId) return false;
    const sub = subjects.find((s) => s.id === c.subjectId);
    const purpose = purposeById(c.purposeId);
    if (filters.vertical !== 'all' && claimVertical(c, sub, purpose) !== filters.vertical) return false;
    if (sub && !subjectOk(sub)) return false;
    return true;
  };

  const fClaims = claims.filter(claimOk);
  const fSubjects = subjects.filter((s) => {
    if (!subjectOk(s)) return false;
    if (filters.goal === 'paying' && !isPaying(s)) return false;
    if (!filters.repId) return true;
    return claims.some((c) => c.subjectId === s.id && c.ownerId === filters.repId);
  });
  const fOutreach = outreaches.filter((o) => {
    if (filters.repId && o.actorId !== filters.repId) return false;
    const sub = subjects.find((s) => s.id === o.subjectId);
    return sub ? subjectOk(sub) : true;
  });

  const scopedReps = filters.repId ? reps.filter((r) => r.id === filters.repId) : reps.filter((r) => r.role !== 'manager');
  const monthlyQuota = scopedReps.reduce((s, r) => s + (r.quotaGhs ?? 0), 0);
  const quotaGhs = paceQuota(monthlyQuota, filters.period);
  const tallySubjects = filters.repId ? fSubjects : subjects.filter(subjectOk);

  const cur = tallyWindow({
    claims: fClaims,
    subjects: tallySubjects,
    outreaches: fOutreach,
    purposeById,
    offerById,
    from,
    to,
    now,
    policy,
    repCount: Math.max(1, scopedReps.length),
    quotaGhs,
  });
  const prev = compared
    ? tallyWindow({
        claims: fClaims,
        subjects: tallySubjects,
        outreaches: fOutreach,
        purposeById,
        offerById,
        from: prevFrom,
        to: prevTo,
        now: prevTo,
        policy,
        repCount: Math.max(1, scopedReps.length),
        quotaGhs,
      })
    : null;

  const kpis = emptyKpis();
  const stock = new Set<KpiId>([
    'pipelineGhs',
    'customers',
    'poolAvailable',
    'capacity',
    'overdue',
    'influencedSpend',
    'pipelineCoverage',
  ]);
  for (const meta of KPI_META) {
    kpis[meta.id] = {
      id: meta.id,
      value: cur.map[meta.id],
      prev: stock.has(meta.id) || !prev ? null : prev.map[meta.id],
    };
  }

  const poolSubjects = subjects.filter((s) => subjectOk(s));
  const researchN = poolSubjects.filter((s) => s.acquisitionStage === 'research').length;
  const customerN = poolSubjects.filter((s) => s.acquisitionStage === 'customer').length;
  const userN = poolSubjects.filter((s) => s.acquisitionStage === 'user').length;
  const prospectN = poolSubjects.filter((s) => s.acquisitionStage === 'prospect').length;

  const earliest = Math.min(
    ...fClaims.map((c) => new Date(c.claimedAt).getTime()),
    ...fOutreach.map((o) => new Date(o.at).getTime()),
    to - 30 * 86400_000,
  );
  const seriesFrom = from > 0 ? from : Number.isFinite(earliest) ? earliest : to - 30 * 86400_000;
  const spanMs = Math.max(1, to - seriesFrom);
  const bucketMs = spanMs > 45 * 86400_000 ? 7 * 86400_000 : 86400_000;
  const series: SeriesPoint[] = [];
  for (let t = seriesFrom; t < to; t += bucketMs) {
    const end = Math.min(t + bucketMs, to);
    const label = new Date(t).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
    const claimed = fClaims.filter((c) => inRange(c.claimedAt, t, end)).length;
    const wonRows = fClaims.filter((c) => c.status === 'won' && c.win && inRange(c.win.at, t, end));
    const wonGhs = wonRows.reduce((s, c) => s + (purposeById(c.purposeId)?.estimatedValueGhs ?? 0), 0);
    const outreach = fOutreach.filter((o) => o.status !== 'draft' && o.status !== 'blocked' && inRange(o.at, t, end)).length;
    series.push({ label, claimed, won: wonRows.length, outreach, wonGhs });
  }

  const named = (id: string, label: string): NamedCount => ({ id, label, n: 0, ghs: 0 });
  const byGoal = SALES_GOALS.map((g) => named(g, SALES_GOAL_LABEL[g]));
  const byVertical = SALES_VERTICALS.map((v) => named(v, VERTICAL_LABEL[v]));
  const bySource: NamedCount[] = (Object.keys(SOURCE_KIND_LABEL) as SourceKind[]).map((k) => named(k, SOURCE_KIND_LABEL[k]));
  const byKind = (['person', 'organization', 'business'] as SubjectKind[]).map((k) => named(k, SUBJECT_KIND_LABEL[k]));
  const desks = SALES_VERTICALS.map((v) => named(v, VERTICAL_LABEL[v]));

  const bump = (row: NamedCount | undefined, ghs: number) => {
    if (!row) return;
    row.n += 1;
    row.ghs += ghs;
  };

  for (const c of cur.live) {
    const sub = subjects.find((s) => s.id === c.subjectId);
    const purpose = purposeById(c.purposeId);
    const ghs = purpose?.estimatedValueGhs ?? 0;
    if (purpose) bump(byGoal.find((r) => r.id === purpose.goal), ghs);
    bump(byVertical.find((r) => r.id === claimVertical(c, sub, purpose)), ghs);
    if (sub) {
      bump(bySource.find((r) => r.id === sourceKind(sub.source)), ghs);
      bump(byKind.find((r) => r.id === sub.kind), ghs);
    }
    bump(desks.find((r) => r.id === claimVertical(c, sub, purpose)), ghs);
  }

  const byChannel = OUTREACH_CHANNELS.map((ch) => {
    const sent = fOutreach.filter((o) => o.channel === ch && o.status !== 'draft' && o.status !== 'blocked' && inRange(o.at, from, to));
    return {
      id: ch,
      label: OUTREACH_CHANNEL_LABEL[ch],
      sent: sent.length,
      replied: sent.filter((o) => o.status === 'replied' || o.feedback).length,
      clicked: sent.filter((o) => o.trackedLinks.some((l) => l.events.some((e) => e.kind === 'click'))).length,
    };
  });

  const outcomeIds = Object.keys(OUTREACH_OUTCOME_LABEL) as OutreachFeedbackOutcome[];
  const byOutcome = outcomeIds.map((id) => ({
    id,
    label: OUTREACH_OUTCOME_LABEL[id],
    n: fOutreach.filter((o) => o.feedback?.outcome === id && inRange(o.at, from, to)).length,
  }));

  const lossReasons = LOSS_REASONS.map((id) => ({
    id,
    label: LOSS_REASON_LABEL[id],
    n: fClaims.filter((c) => c.status === 'lost' && c.lossReason === id && inRange(c.claimedAt, from, to)).length,
  }));

  const soon = now + 48 * 3600_000;
  const idleMs = policy.idleDays * 86400_000;
  const idle: SalesClaim[] = [];
  const expiringSoon: SalesClaim[] = [];
  for (const c of cur.live) {
    const exp = new Date(c.expiresAt).getTime();
    if (exp > now && exp <= soon) expiringSoon.push(c);
    const last = lastOutreachAt(c.subjectId, fOutreach);
    const ref = last ?? new Date(c.claimedAt).getTime();
    if (now - ref >= idleMs) idle.push(c);
  }

  const agents: AgentInsight[] = reps
    .filter((r) => (filters.repId ? r.id === filters.repId : true))
    .map((r) => {
      const mine = fClaims.filter((c) => c.ownerId === r.id);
      const live = mine.filter((c) => ACTIVE_CLAIM_STATUSES.includes(c.status));
      const wonRows = mine.filter((c) => c.status === 'won' && c.win && inRange(c.win.at, from, to));
      const wonGhs = wonRows.reduce((s, c) => s + (purposeById(c.purposeId)?.estimatedValueGhs ?? 0), 0);
      const sent = fOutreach.filter((o) => o.actorId === r.id && o.status !== 'draft' && o.status !== 'blocked' && inRange(o.at, from, to));
      const firstHits = mine.filter((c) => inRange(c.claimedAt, from, to));
      let slaHits = 0;
      let slaDenom = 0;
      for (const c of firstHits) {
        const first = fOutreach
          .filter((o) => o.claimId === c.id && o.status !== 'draft' && o.status !== 'blocked')
          .sort((a, b) => a.at.localeCompare(b.at))[0];
        const windowEnd = new Date(c.claimedAt).getTime() + policy.firstTouchHours * 3600_000;
        if (first) {
          slaDenom += 1;
          if (new Date(first.at).getTime() <= windowEnd) slaHits += 1;
        } else if (now >= windowEnd) slaDenom += 1;
      }
      const spend = subjects
        .filter((s) => mine.some((c) => c.subjectId === s.id))
        .reduce((s, sub) => s + (sub.platform?.lifetimePaidGhs ?? 0), 0);
      return {
        id: r.id,
        name: r.name,
        role: r.role,
        title: r.title,
        avatarColor: r.avatarColor,
        quotaGhs: r.quotaGhs ?? 0,
        wonGhs,
        pipelineGhs: live.reduce((s, c) => s + (purposeById(c.purposeId)?.estimatedValueGhs ?? 0), 0),
        influencedSpendGhs: spend,
        won: wonRows.length,
        outreachSent: sent.length,
        replyRate: ratio(sent.filter((o) => o.feedback || o.status === 'replied').length, sent.length),
        slaHitRate: slaDenom ? slaHits / slaDenom : 0,
        active: live.length,
        overdue: live.filter((c) => new Date(c.expiresAt).getTime() <= now).length,
        attainment: r.quotaGhs ? wonGhs / paceQuota(r.quotaGhs, filters.period) : 0,
      };
    })
    .sort((a, b) => b.attainment - a.attainment || b.wonGhs - a.wonGhs);

  const frozen = poolSubjects.filter(
    (s) => isInPool(s) && (s.doNotContact || (s.subjectCooldownUntil && new Date(s.subjectCooldownUntil).getTime() > now)),
  ).length;
  const avail = poolSubjects.filter((s) => isInPool(s)).length;

  const outcomeClaims = claims.filter(claimOkExceptGoal);
  const outcomeSubjects = poolSubjects.filter((s) => isInPool(s));
  const payingNow = new Set(outcomeSubjects.filter(isPaying).map((s) => s.id));
  const users = tallyGoal(outcomeClaims, 'signup', from, to, purposeById);
  const listers = tallyGoal(outcomeClaims, 'supply', from, to, purposeById);
  const advertisers = tallyGoal(outcomeClaims, 'monetize', from, to, purposeById);
  const paying = tallyPaying(outcomeClaims, payingNow, from, to);
  const prevUsers = compared ? tallyGoal(outcomeClaims, 'signup', prevFrom, prevTo, purposeById) : null;
  const prevListers = compared ? tallyGoal(outcomeClaims, 'supply', prevFrom, prevTo, purposeById) : null;
  const prevAdvertisers = compared ? tallyGoal(outcomeClaims, 'monetize', prevFrom, prevTo, purposeById) : null;
  const prevPaying = compared ? tallyPaying(outcomeClaims, payingNow, prevFrom, prevTo) : null;
  const payingLive = outcomeClaims.filter((c) => ACTIVE_CLAIM_STATUSES.includes(c.status) && !payingNow.has(c.subjectId)).length;

  const outcomeSeries: OutcomeSeriesPoint[] = [];
  for (let t = seriesFrom; t < to; t += bucketMs) {
    const end = Math.min(t + bucketMs, to);
    const label = new Date(t).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
    outcomeSeries.push({
      label,
      users: tallyGoal(outcomeClaims, 'signup', t, end, purposeById).rate,
      listers: tallyGoal(outcomeClaims, 'supply', t, end, purposeById).rate,
      paying: tallyPaying(outcomeClaims, payingNow, t, end).rate,
      advertisers: tallyGoal(outcomeClaims, 'monetize', t, end, purposeById).rate,
    });
  }

  const outcomes: OutcomeInsight[] = [
    {
      id: 'users',
      label: 'Users',
      hint: 'Became a FeelyFeely user',
      goal: 'signup',
      ...users,
      prevWon: prevUsers?.won ?? null,
      prevRate: prevUsers?.rate ?? null,
      stock: outcomeSubjects.filter(onPlatform).length,
      history: outcomeSeries.map((p) => p.users),
    },
    {
      id: 'listers',
      label: 'Listers',
      hint: 'Posted a live listing',
      goal: 'supply',
      ...listers,
      prevWon: prevListers?.won ?? null,
      prevRate: prevListers?.rate ?? null,
      stock: outcomeSubjects.filter((s) => (s.platform?.listingCount ?? 0) > 0).length,
      history: outcomeSeries.map((p) => p.listers),
    },
    {
      id: 'paying',
      label: 'Paying',
      hint: 'Closed pursuits who now spend on FeelyFeely',
      goal: null,
      won: paying.won,
      claimed: paying.closed,
      closed: paying.closed,
      rate: paying.rate,
      prevWon: prevPaying?.won ?? null,
      prevRate: prevPaying?.rate ?? null,
      inPlay: payingLive,
      stock: payingNow.size,
      history: outcomeSeries.map((p) => p.paying),
    },
    {
      id: 'advertisers',
      label: 'Advertisers',
      hint: 'Started paying for promotion',
      goal: 'monetize',
      ...advertisers,
      prevWon: prevAdvertisers?.won ?? null,
      prevRate: prevAdvertisers?.rate ?? null,
      stock: outcomeSubjects.filter((s) =>
        claims.some((c) => c.subjectId === s.id && c.status === 'won' && c.win?.goal === 'monetize'),
      ).length,
      history: outcomeSeries.map((p) => p.advertisers),
    },
  ];

  return {
    fromMs: from,
    toMs: to,
    prevFromMs: prevFrom,
    prevToMs: prevTo,
    kpis,
    funnel: cur.funnel,
    acquisition: [
      { id: 'research', label: ACQUISITION_STAGE_LABEL.research, n: researchN },
      { id: 'prospect', label: ACQUISITION_STAGE_LABEL.prospect, n: prospectN },
      { id: 'user', label: ACQUISITION_STAGE_LABEL.user, n: userN },
      { id: 'customer', label: ACQUISITION_STAGE_LABEL.customer, n: customerN },
    ],
    outcomes,
    series,
    outcomeSeries,
    byGoal,
    byVertical,
    bySource,
    byKind,
    byChannel,
    byOutcome,
    lossReasons,
    desks,
    offers: input.offers.map((off) => {
      const wonRows = fClaims.filter((c) => c.offerId === off.id && c.status === 'won' && c.win && inRange(c.win.at, from, to));
      return {
        id: off.id,
        name: off.name,
        n: wonRows.length,
        costGhs: wonRows.length * off.costGhs,
        wonGhs: wonRows.reduce((s, c) => s + (purposeById(c.purposeId)?.estimatedValueGhs ?? 0), 0),
      };
    }),
    agents,
    overdue: cur.overdueClaims,
    expiringSoon,
    idle,
    recentWins: fClaims
      .filter((c) => c.status === 'won' && c.win)
      .sort((a, b) => (b.win?.at ?? '').localeCompare(a.win?.at ?? ''))
      .slice(0, 8),
    dueOutreach: fOutreach
      .filter((o) => o.nextStepDue && new Date(o.nextStepDue).getTime() <= now + 48 * 3600_000 && o.status !== 'draft')
      .sort((a, b) => (a.nextStepDue ?? '').localeCompare(b.nextStepDue ?? ''))
      .slice(0, 8),
    offerCostGhs: cur.map.offerCost ?? 0,
    wonGhs: cur.map.wonGhs ?? 0,
    quotaGhs,
    liveN: cur.live.length,
    freezePct: ratio(frozen, avail),
    liveBook: ACTIVE_CLAIM_STATUSES.map((id) => ({
      id,
      label: CLAIM_STATUS_LABEL[id],
      n: cur.live.filter((c) => c.status === id).length,
    })),
    remainingGhs: Math.max(0, quotaGhs - (cur.map.wonGhs ?? 0)),
    coverage: cur.map.pipelineCoverage ?? 0,
  };
}

export function paceQuota(monthlyGhs: number, period: PerformancePeriod): number {
  if (period === '7d') return Math.round((monthlyGhs * 7) / 30);
  if (period === '90d') return monthlyGhs * 3;
  return monthlyGhs;
}

export function quotaCaption(period: PerformancePeriod): string {
  if (period === '7d') return '7-day slice of monthly quota';
  if (period === '90d') return 'three months of quota';
  if (period === 'all') return 'monthly quota (unpaced)';
  return 'monthly quota';
}

export function deltaPct(curr: number | null, prev: number | null): number | null {
  if (curr == null || prev == null) return null;
  if (Math.abs(prev) < 1 && Math.abs(curr) < 1) return null;
  if (prev === 0) return null;
  return (curr - prev) / Math.abs(prev);
}

export type Diagnosis = {
  tone: 'bad' | 'warn' | 'ok';
  headline: string;
  detail: string;
};

export function diagnose(ins: DashInsights): Diagnosis {
  const quota = ins.quotaGhs;
  const won = ins.wonGhs;
  const pipeline = ins.kpis.pipelineGhs.value ?? 0;
  const remaining = ins.remainingGhs;
  const coverage = ins.coverage;
  const attain = quota > 0 ? won / quota : 0;
  const winRate = ins.kpis.winRate.value ?? 0;
  const sla = ins.kpis.slaHitRate.value ?? 0;
  const claimed = ins.kpis.claimed.value ?? 0;
  const closed = (ins.kpis.won.value ?? 0) + (ins.kpis.lost.value ?? 0);
  const desks = [...ins.desks].sort((a, b) => b.ghs - a.ghs);
  const top = desks[0];
  const deskTotal = desks.reduce((s, d) => s + d.ghs, 0);
  const deskShare = top && deskTotal > 0 ? top.ghs / deskTotal : 0;
  const emptyDesks = desks.filter((d) => d.n === 0).map((d) => d.label);
  const slaNote = claimed > 0 && sla < 0.5 ? ` First-touch SLA ${Math.round(sla * 100)}%.` : '';
  const coverNote =
    remaining > 0
      ? ` ${formatCompact(pipeline)} in play covers ${Math.round(coverage * 100)}% of the remaining ${formatCompact(remaining)}.`
      : '';

  if (ins.overdue.length) {
    return {
      tone: 'bad',
      headline: `${ins.overdue.length} claim${ins.overdue.length === 1 ? '' : 's'} past protection`,
      detail: `Release or they stay locked dead.${coverNote}${slaNote}`,
    };
  }
  if (coverage < 0.4 && attain < 0.85 && quota > 0) {
    return {
      tone: 'bad',
      headline: 'Not enough in play to hit quota',
      detail: `Won ${formatCompact(won)} of ${formatCompact(quota)}. Even a perfect close on the live book misses.${slaNote}`,
    };
  }
  if (sla < 0.5 && claimed > 0) {
    return {
      tone: 'bad',
      headline: 'First-touch is the leak',
      detail: `SLA ${Math.round(sla * 100)}% on ${claimed} claim${claimed === 1 ? '' : 's'} this window. Touched ${ins.funnel.find((s) => s.id === 'touched')?.n ?? 0} of ${claimed}.${coverNote}`,
    };
  }
  if (winRate >= 0.55 && attain < 0.5 && closed >= 2) {
    return {
      tone: 'warn',
      headline: 'Closing is fine. Volume is not.',
      detail: `Won ${ins.kpis.won.value ?? 0} of ${closed} closed (${Math.round(winRate * 100)}%), but ${formatCompact(won)} against ${formatCompact(quota)}.${coverNote}`,
    };
  }
  if (deskShare >= 0.8 && pipeline > 0 && top) {
    return {
      tone: 'warn',
      headline: `${top.label} is carrying the floor`,
      detail: `${formatCompact(top.ghs)} of ${formatCompact(deskTotal)} in play is ${top.label}.${
        emptyDesks.length ? ` ${emptyDesks.join(', ')} ${emptyDesks.length === 1 ? 'has' : 'have'} nothing live.` : ''
      }`,
    };
  }
  if (attain >= 0.9) {
    return {
      tone: 'ok',
      headline: 'On pace',
      detail: `Won ${formatCompact(won)} of ${formatCompact(quota)} (${Math.round(attain * 100)}%).${coverNote}`,
    };
  }
  if (attain >= 0.5) {
    return {
      tone: 'warn',
      headline: 'Behind, but recoverable',
      detail: `Won ${formatCompact(won)} of ${formatCompact(quota)} (${Math.round(attain * 100)}%).${coverNote}${slaNote}`,
    };
  }
  return {
    tone: attain > 0 ? 'warn' : 'bad',
    headline: 'Behind quota',
    detail: `Won ${formatCompact(won)} of ${formatCompact(quota)}.${coverNote}${slaNote}`,
  };
}

export function liveValueGhs(
  subjectId: string,
  claims: SalesClaim[],
  purposeById: (id: string) => SalesPurpose | undefined,
): number {
  const live = claims.find((c) => c.subjectId === subjectId && ACTIVE_CLAIM_STATUSES.includes(c.status));
  const row = live ?? claims.find((c) => c.subjectId === subjectId);
  return row ? (purposeById(row.purposeId)?.estimatedValueGhs ?? 0) : 0;
}

function formatCompact(n: number): string {
  if (n >= 10_000) return `GHS ${Math.round(n / 1_000)}k`;
  return `GHS ${Math.round(n).toLocaleString('en-GB')}`;
}

export function breakdownRows(ins: DashInsights, groupBy: DashGroupBy): NamedCount[] {
  if (groupBy === 'goal') return ins.byGoal;
  if (groupBy === 'source') return ins.bySource;
  if (groupBy === 'kind') return ins.byKind;
  return ins.byVertical;
}
